<template>
	<div>
		<ListLayout
			:title="t('admin_overview_integrations_title')"
			:buttonText="t('admin_integrations_add_connection')"
			@click="onChangeTab({ id: 'AVAILABLE' })"
		>
			<template #search>
				<div></div>
			</template>

			<template #custom>
				<div class="tabs-wrapper">
					<Tabs
						class="integration-tabs"
						stretch
						:tabs="tabs"
						:defaultSelected="activeType"
						selectedcolor="#2c2d31"
						@change="onChangeTab"
					>
						<template v-slot="{ tab }">
							<div class="integration-tab" v-text="tab.text" />
						</template>
					</Tabs>
				</div>
			</template>

			<template #table>
				<Table
					v-if="activeType === 'CONNECTED'"
					:columns="columns"
					:rows="connections"
					:loadingOverlay="loading"
					infinite
					clickable
					loadMoreTrigger="0px"
					ref="table"
					height="calc(100vh - 183px)"
					@row-click="showConnection"
					class="integrations-table"
				>
					<template #cell-name="{ data }">
						<div class="row">
							<div class="info">
								<div class="logo">
									<img
										:src="data.integration.icon_url"
										alt="integration icon"
									/>
								</div>
								<div>
									<div class="integration-title">
										{{ data.integration.display_name }}
									</div>
									<div class="account-info">
										{{ data.label }}
									</div>
								</div>
							</div>
						</div>
					</template>

					<template #cell-connected_on="{ data }">
						{{ moment(data.created_at).format('MM/DD/YYYY') }}
					</template>

					<template #cell-last_sync="{ data }">
						{{
							data.last_sync === null
								? t('admin_integrations_never')
								: moment(data.last_sync).format('MM/DD/YYYY')
						}}
					</template>

					<template #cell-status="{ data }">
						{{ data.last_sync_status }}
					</template>

					<template #empty>
						<div class="empty-state">
							<img
								class="empty-state-icon"
								src="/images/common/integrations_empty.png"
								alt="Empty Integrations Icon"
							/>
							<div class="title" v-text="t('admin_integrations_empty_title')" />
							<Button primary @click="onChangeTab({ id: 'AVAILABLE' })">{{
								t('admin_integrations_browseintegrations_label')
							}}</Button>
						</div>
					</template>
				</Table>

				<div v-if="activeType === 'AVAILABLE'" class="option-cards">
					<div
						v-for="option in integrations"
						:key="option.id"
						class="option-card"
					>
						<div class="card-content">
							<div class="option-img">
								<img :src="option.icon_url" />
							</div>
							<div class="title">{{ option.display_name }}</div>
						</div>

						<div class="card-divider"></div>

						<div class="card-footer">
							<Button link class="learn-more-link">
								{{ t('admin_integrations_learn_more') }}
							</Button>
							<Button
								class="connect-button"
								@click="connectIntegration(option)"
							>
								{{ t('admin_integrations_connect') }}
							</Button>
						</div>
					</div>
				</div>
			</template>
		</ListLayout>
	</div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Table, Button, Tabs, useAlert } from '@/components/common'
import { ListLayout } from '../common'
import { integrationGet } from '@/api/integrations'
import moment from 'moment'

const types = {
	CONNECTED: 'CONNECTED',
	AVAILABLE: 'AVAILABLE',
}

// Utilities
const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const alert = useAlert()

// Reactive Variables
const activeType = ref(route.params.type || types.CONNECTED)
const loading = ref(false)
const loadingIntegrations = ref(false)
const connections = ref([])
const integrations = ref([])
const table = ref(null)

const tabs = computed(() => [
	{
		id: types.CONNECTED,
		text: t('admin_integrations_connected', {
			Count: connections.value.length,
		}),
	},
	{ id: types.AVAILABLE, text: t('admin_integrations_available') },
])

const columns = ref([
	{
		field: 'name',
		label: t('admin_people_name_label'),
		width: '100%',
		minWidth: '200px',
	},
	{
		field: 'id',
		label: 'ID',
		width: '170px',
	},
	{
		field: 'connected_on',
		label: t('admin_integrations_connected_on'),
	},

	{
		field: 'last_sync',
		label: t('admin_integrations_last_synced'),
	},
	{
		field: 'status',
		label: t('admin_integrations_status'),
	},
])

onMounted(async () => {
	await fetchConnections()
	await fetchIntegrations()
})

// Methods
function onChangeTab({ id }) {
	activeType.value = id
}

async function fetchConnections() {
	loading.value = true
	try {
		const response = await integrationGet('/connections')
		connections.value = response.data
		table.value?.scrollToTop()

		// CODE BLOAT DUE TO SERVER RESPONSE: Fetch registration status in parallel
		// the is_registered flag is not returned by the server, so we need to fetch it manually for each connection
		const registrationPromises = connections.value.map(
			async (connection, index) => {
				try {
					const regResponse = (
						await integrationGet(
							`/${connection.integration.type}/configure/${connection.id}`
						)
					).data

					// Update the specific connection in place
					connections.value[index] = {
						...connections.value[index],
						is_registered:
							'is_registered' in regResponse ? regResponse.is_registered : null,
						platform: regResponse.platform || null,
					}
				} catch (error) {
					console.error(
						`Failed to fetch registration for connection ${connection.id}:`,
						error
					)
				}
			}
		)

		await Promise.all(registrationPromises)
		loading.value = false
	} catch (e) {
		console.error(e)
		loading.value = false
		alert.error(t('common_api_error_save'))
	}
}

async function fetchIntegrations() {
	loadingIntegrations.value = true
	try {
		const response = await integrationGet('')
		integrations.value = response.data
		loadingIntegrations.value = false
	} catch (e) {
		console.error(e)
		loadingIntegrations.value = false
		alert.error(t('common_api_error_save'))
	}
}

function showConnection(connection) {
	if (connection.is_registered === false) {
		router.push({
			name: 'configuration',
			params: { id: connection.integration.id },
			query: {
				connectionId: connection.id,
				platform: connection.platform,
				integrationObject: JSON.stringify(connection.integration),
			},
		})
	} else {
		router.push({
			name: 'connection',
			params: { id: connection.id },
			query: { integrationType: connection.integration.type },
		})
	}
}

function connectIntegration(option) {
	router.push({
		name: 'configuration',
		params: { id: option.id },
		query: { integrationObject: JSON.stringify(option) },
	})
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

:deep(.list-layout) {
	header {
		height: 146px;
		border-bottom: solid 1px var(--neutral-200);
	}

	.empty-state {
		display: flex;
		align-items: center;
		flex-direction: column;
		.title {
			font-size: 14px;
			font-family: 'Avenir-Medium';
			color: var(--text-gray-medium);
			margin: 25px 0 15px 0;
		}
	}

	.row {
		display: flex;
		align-items: center;
		width: 100%;
		.info {
			display: flex;
			align-items: center;
			gap: 15px;

			.logo {
				height: 40px;
				width: 40px;
				border: solid 1px var(--neutral-200);
				padding: 2.5px;
				display: flex;
				align-items: center;
				justify-content: center;
				border-radius: 6.5px;
				img {
					border-radius: 6.5px;
					object-fit: cover;
					height: 100%;
					width: 100%;
				}
			}

			.integration-title {
				font-family: 'Avenir-Book';
				color: var(--neutral-800);
				font-size: 14px;
			}

			.account-info {
				font-size: 12px;
				color: var(--neutral-500);
			}
		}

		.status {
			display: flex;
			align-items: center;
			gap: 20px;
			.status-icon {
				color: var(--teal-300);
			}
			.gear-icon {
				font-size: 20px;
				color: var(--neutral-400);
				margin-left: 20px;
			}
		}
	}

	.option-cards {
		display: flex;
		padding: 25px;
		gap: 40px;
		background: var(--neutral-0);
		flex-wrap: wrap;

		.option-card {
			width: 310px;
			border: 1px solid var(--neutral-200);
			border-radius: 12px;
			overflow: hidden;
			display: flex;
			flex-direction: column;

			.card-content {
				padding: 20px;
				flex: 1;

				.option-img {
					width: 56px;
					height: 56px;
					margin-bottom: 16px;
					display: flex;
					align-items: center;
					justify-content: center;

					img {
						width: 100%;
						height: 100%;
						object-fit: contain;
						border-radius: 6px;
					}
				}

				.title {
					font-family: 'Avenir-Heavy';
					font-size: 16px;
					color: var(--neutral-800);
					line-height: 1.4;
				}
			}

			.card-divider {
				height: 1px;
				background-color: var(--neutral-200);
			}

			.card-footer {
				padding: 10px 10px;
				display: flex;
				align-items: center;
				justify-content: space-between;

				.learn-more-link {
					color: var(--blue-300);
					text-decoration: none;
					font-size: 14px;
					font-family: 'Avenir-Medium';
					padding: 0 10px;
				}
			}
		}
	}
}

.tabs-wrapper {
	background-color: #f9f9f9;
	margin-left: -24px;
	margin-right: -24px;
	.tabs-container {
		border-bottom: none;
	}

	.integration-tabs {
		width: 400px;
	}
}

.empty-icon {
	font-size: 200px;
}
</style>
